<template>
  <v-main>
    <v-container fluid>
      <!-- Professional Form Card -->
      <v-row>
        <v-col cols="12">
          <v-card elevation="3" class="form-card">
            <!-- Page Header integrated into Form Card -->
            <v-card-title class="page-header py-2 px-3">
              <v-row class="w-100 ma-0" align="center">
                <!-- Left Side: Title and Description -->
                <v-col cols="12" md="8" class="pa-0">
                  <div class="d-flex align-center">
                    <div>
                      <h1 class="page-title primary--text">
                        Add New Seller Product
                      </h1>
                      <p class="page-subtitle grey--text text--darken-1 mb-0">
                        Assign a product to a seller for commission-based sales
                      </p>
                    </div>
                  </div>
                </v-col>

                <!-- Right Side: Back Button and Form Badge -->
                <v-col cols="12" md="4" class="pa-0">
                  <div class="d-flex align-center justify-end">
                    <v-chip color="info" outlined x-small class="mr-2">
                      <v-icon left x-small>mdi-account-plus</v-icon>
                      New Assignment
                    </v-chip>
                    <v-btn
                      color="grey darken-1"
                      outlined
                      @click="$router.back()"
                      class="back-button"
                    >
                      <v-icon left>mdi-arrow-left</v-icon>
                      Back to Seller Products
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-card-title>

            <v-divider></v-divider>

            <!-- Form Content -->
            <v-card-text class="form-content px-2 py-2">
              <v-form ref="form" v-model="valid" lazy-validation>
                <!-- Assignment Information Section -->
                <div class="form-section mb-2">
                  <h3 class="section-title mb-1">
                    <v-icon color="primary" class="mr-2"
                      >mdi-account-arrow-right</v-icon
                    >
                    Assignment Information
                  </h3>

                  <v-row>
                    <v-col cols="12" md="6">
                      <v-autocomplete
                        v-model="form.user_id"
                        :items="userItems"
                        label="Seller"
                        placeholder="Select a seller"
                        outlined
                        :rules="[(v) => !!v || 'Seller is required']"
                        :error-messages="errors.user_id"
                        @input="clearError('user_id')"
                        class="professional-field"
                        hide-details="auto"
                        required
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-account</v-icon>
                        </template>
                      </v-autocomplete>
                    </v-col>

                    <v-col cols="12" md="6">
                      <v-autocomplete
                        v-model="form.product_id"
                        :items="productItems"
                        label="Product"
                        placeholder="Select a product"
                        outlined
                        :rules="[(v) => !!v || 'Product is required']"
                        :error-messages="errors.product_id"
                        @input="onProductChange"
                        class="professional-field"
                        hide-details="auto"
                        required
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-package</v-icon>
                        </template>
                      </v-autocomplete>
                    </v-col>
                  </v-row>
                </div>

                <!-- Account Settings Section -->
                <div class="form-section mb-2">
                  <h3 class="section-title mb-1">
                    <v-icon color="primary" class="mr-2">mdi-cog</v-icon>
                    Assignment Settings
                  </h3>

                  <v-row>
                    <v-col cols="12" md="6">
                      <v-card outlined class="status-card">
                        <v-card-text class="pa-2">
                          <div class="d-flex align-center">
                            <v-icon color="grey" class="mr-3"
                              >mdi-check-circle</v-icon
                            >
                            <div class="flex-grow-1">
                              <div class="font-weight-medium mb-1">
                                Assignment Status
                              </div>
                              <div class="text-caption grey--text">
                                Set the initial status for this assignment
                              </div>
                            </div>
                            <v-switch
                              v-model="form.is_active"
                              color="success"
                              inset
                              hide-details
                              class="ml-3"
                              @change="clearError('is_active')"
                            ></v-switch>
                          </div>
                          <div class="mt-2">
                            <v-chip
                              small
                              :color="form.is_active ? 'success' : 'warning'"
                              :text-color="form.is_active ? 'white' : 'black'"
                            >
                              {{ form.is_active ? "Active" : "Inactive" }}
                            </v-chip>
                          </div>
                        </v-card-text>
                      </v-card>
                    </v-col>
                  </v-row>
                </div>
                <!-- Product Variants Section -->
                <div
                  v-if="form.product_id && productVariants.length > 0"
                  class="form-section mb-2"
                >
                  <h3 class="section-title mb-1">
                    <v-icon color="primary" class="mr-2"
                      >mdi-package-variant</v-icon
                    >
                    Available Product Variants
                  </h3>

                  <div class="variants-container">
                    <div
                      v-for="(variant, index) in productVariants"
                      :key="variant.id"
                      class="variant-card mb-3"
                    >
                      <v-card outlined class="variant-selection-card">
                        <v-card-text class="pa-3">
                          <v-row align="center">
                            <v-col cols="12" md="1" class="text-center">
                              <v-checkbox
                                v-model="variant.selected"
                                @change="toggleVariantSelection(variant)"
                                color="primary"
                                hide-details
                              ></v-checkbox>
                            </v-col>

                            <v-col cols="12" md="4">
                              <div class="variant-info">
                                <h4 class="variant-name">
                                  {{ variant.name || `Variant ${index + 1}` }}
                                </h4>
                                <div class="text-caption grey--text">
                                  SKU: {{ variant.sku_id }}
                                </div>
                                <div
                                  class="text-caption grey--text"
                                  v-if="variant.unit"
                                >
                                  Unit: {{ variant.unit.description }}
                                </div>
                              </div>
                            </v-col>

                            <v-col cols="12" md="7" v-if="variant.selected">
                              <v-row>
                                <v-col cols="12" md="2">
                                  <v-text-field
                                    v-model="variant.maximum_retail_price"
                                    label="MRP"
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    outlined
                                    dense
                                    :rules="[
                                      (v) => !!v || 'MRP is required',
                                    ]"
                                    class="compact-field"
                                    hide-details="auto"
                                  >
                                    <template v-slot:prepend-inner>
                                      <v-icon color="grey" small>mdi-currency-rupee</v-icon>
                                    </template>
                                  </v-text-field>
                                </v-col>

                                <v-col cols="12" md="2">
                                  <v-text-field
                                    v-model="variant.seller_discount"
                                    label="Seller Disc."
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    outlined
                                    dense
                                    class="compact-field"
                                    hide-details="auto"
                                  >
                                    <template v-slot:prepend-inner>
                                      <v-icon color="grey" small>mdi-tag-minus</v-icon>
                                    </template>
                                  </v-text-field>
                                </v-col>

                                <v-col cols="12" md="2">
                                  <v-text-field
                                    v-model="variant.ht_discount"
                                    label="HT Disc."
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    outlined
                                    dense
                                    class="compact-field"
                                    hide-details="auto"
                                  >
                                    <template v-slot:prepend-inner>
                                      <v-icon color="grey" small>mdi-sale</v-icon>
                                    </template>
                                  </v-text-field>
                                </v-col>

                                <v-col cols="12" md="2">
                                  <v-text-field
                                    v-model="variant.consumer_discount"
                                    label="Consumer Disc."
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    outlined
                                    dense
                                    class="compact-field"
                                    hide-details="auto"
                                  >
                                    <template v-slot:prepend-inner>
                                      <v-icon color="grey" small>mdi-account-cash</v-icon>
                                    </template>
                                  </v-text-field>
                                </v-col>

                                <v-col cols="12" md="2">
                                  <v-text-field
                                    v-model="variant.doctor_discount"
                                    label="Doctor Disc."
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    outlined
                                    dense
                                    class="compact-field"
                                    hide-details="auto"
                                  >
                                    <template v-slot:prepend-inner>
                                      <v-icon color="grey" small>mdi-medical-bag</v-icon>
                                    </template>
                                  </v-text-field>
                                </v-col>
                              </v-row>

                              <!-- Second Row for Stock -->
                              <v-row class="mt-2">
                                <v-col cols="12" md="6">
                                  <v-text-field
                                    v-model="variant.stock"
                                    label="Stock (Optional)"
                                    type="number"
                                    min="0"
                                    outlined
                                    dense
                                    class="compact-field"
                                    hide-details="auto"
                                    placeholder="0"
                                  >
                                    <template v-slot:prepend-inner>
                                      <v-icon color="grey" small>mdi-package-variant</v-icon>
                                    </template>
                                  </v-text-field>
                                </v-col>
                              </v-row>
                            </v-col>
                          </v-row>

                          <v-row v-if="variant.selected" class="mt-2">
                            <v-col cols="12">
                              <div class="variant-details text-caption">
                                <v-chip
                                  x-small
                                  color="info"
                                  outlined
                                  class="mr-2"
                                  >MRP: ${{
                                    variant.maximum_retail_price || 0
                                  }}</v-chip
                                >
                                <v-chip
                                  x-small
                                  color="orange"
                                  outlined
                                  class="mr-2"
                                  v-if="variant.seller_discount_percentage"
                                  >Seller Discount: {{ variant.seller_discount_percentage }}%</v-chip
                                >
                                <v-chip
                                  x-small
                                  color="primary"
                                  outlined
                                  class="mr-2"
                                  v-if="variant.ht_discount_percentage"
                                  >HT Discount: {{ variant.ht_discount_percentage }}%</v-chip
                                >
                                <v-chip
                                  x-small
                                  color="grey"
                                  outlined
                                  class="mr-2"
                                  v-if="variant.packing_type"
                                  >{{
                                    variant.packing_type.description
                                  }}</v-chip
                                >
                                <v-chip
                                  x-small
                                  color="grey"
                                  outlined
                                  class="mr-2"
                                  v-if="variant.hsn_code"
                                  >HSN: {{ variant.hsn_code }}</v-chip
                                >
                              </div>
                            </v-col>
                          </v-row>
                        </v-card-text>
                      </v-card>
                    </div>
                  </div>
                </div>

              </v-form>
            </v-card-text>

            <!-- Form Actions Footer -->
            <v-divider></v-divider>
            <v-card-actions class="form-actions pa-2">
              <v-row class="w-100 ma-0" align="center">
                <v-col cols="12" md="8" class="pa-0">
                  <div class="d-flex align-center">
                    <v-chip
                      small
                      :color="hasValidationErrors() ? 'error' : 'success'"
                      :text-color="hasValidationErrors() ? 'white' : 'white'"
                      class="mr-3"
                    >
                      <v-icon left x-small>
                        {{
                          hasValidationErrors()
                            ? "mdi-alert-circle"
                            : "mdi-check-circle"
                        }}
                      </v-icon>
                      {{ getValidationMessage() }}
                    </v-chip>
                    <span class="text-caption grey--text">
                      All required fields must be completed
                    </span>
                  </div>
                </v-col>

                <v-col cols="12" md="4" class="pa-0">
                  <div class="d-flex justify-end">
                    <v-btn
                      color="grey"
                      outlined
                      class="mr-3 action-button"
                      @click="$router.back()"
                    >
                      Cancel
                    </v-btn>
                    <v-btn
                      color="primary"
                      class="action-button"
                      @click="save"
                      :loading="isLoading"
                      :disabled="isLoading || hasValidationErrors()"
                    >
                      <v-icon left>mdi-content-save</v-icon>
                      Save Assignment
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </v-main>
</template>

<script>
import axios from "@/axios";

export default {
  name: "SellerProductCreatePage",
  data() {
    return {
      valid: false,
      form: {
        user_id: null,
        product_id: null,
        is_active: true,
      },
      errors: {},
      userItems: [],
      productItems: [],
      productVariants: [],
      selectedVariants: [],
      isLoading: false,
      hasAttemptedVariantSelection: false,
    };
  },
  watch: {
    "form.user_id"() {
      this.clearError("user_id");
    },
    "form.product_id"() {
      this.clearError("product_id");
    },
    "form.is_active"() {
      this.clearError("is_active");
    },
  },
  mounted() {
    this.getMasters();
  },
  methods: {
    async getMasters() {
      try {
        const response = await axios.get("/masters?context=seller_product");
        
        if (response.data) {
          // Populate users (sellers) with null-safe operations
          this.userItems = (response.data.users || []).map((user) => ({
            value: user.id,
            text: `${user.first_name} ${user.last_name} (${user.email})`,
            id: user.id,
          }));

          // Populate products with null-safe operations
          this.productItems = (response.data.products || []).map((product) => ({
            value: product.id,
            text: `${product.name}${
              product.sku_id ? " (" + product.sku_id + ")" : ""
            }`,
            id: product.id,
          }));

        }
      } catch (error) {
        console.error("Error loading masters:", error);
        if (error.response && error.response.status === 401) {
          this.$router.push("/login");
        } else {
          this.$store.dispatch("errors/setError", {
            message: "Failed to load form data. Please refresh the page.",
            type: "error",
          });
        }
      }
    },


    clearError(field) {
      if (this.errors[field]) {
        this.$delete(this.errors, field);
      }
    },

    hasValidationErrors() {
      // Check backend validation errors
      if (Object.keys(this.errors).length > 0) {
        return true;
      }
      
      // Check form validation
      if (!this.valid) {
        return true;
      }
      
      // Only show variant validation errors if user has selected variants
      if (this.hasSelectedVariants) {
        // Check if selected existing variants have required pricing fields
        const selectedExistingVariants = this.productVariants.filter(v => v.selected);
        for (const variant of selectedExistingVariants) {
          if (!variant.maximum_retail_price) {
            return true;
          }
        }
      }
      
      return false;
    },

    getValidationMessage() {
      // Check backend validation errors
      if (Object.keys(this.errors).length > 0) {
        return "Please fix server validation errors";
      }
      
      // Check form validation
      if (!this.valid) {
        return "Please complete all required fields";
      }
      
      // Only show variant validation messages if user has selected variants
      if (this.hasSelectedVariants) {
        // Check if selected existing variants have required pricing fields
        const selectedExistingVariants = this.productVariants.filter(v => v.selected);
        for (const variant of selectedExistingVariants) {
          if (!variant.maximum_retail_price) {
            return `Please set maximum retail price for variant: ${variant.name}`;
          }
        }
      }
      
      return "Form validation passed";
    },

    async onProductChange(productId) {
      this.clearError("product_id");
      this.hasAttemptedVariantSelection = false; // Reset when product changes
      if (productId) {
        await this.loadProductVariants(productId);
      } else {
        this.productVariants = [];
        this.selectedVariants = [];
      }
    },

    async loadProductVariants(productId) {
      try {
        const response = await axios.get("/seller_products/product_variants", {
          params: { product_id: productId },
        });

        if (response.data.success) {
          this.productVariants = response.data.data.map((variant) => ({
            ...variant,
            selected: false,
            maximum_retail_price: "",
            seller_discount: "",
            ht_discount: "",
            consumer_discount: "",
            doctor_discount: "",
            stock: 0,
          }));
        }
      } catch (error) {
        console.error("Error loading product variants:", error);
      }
    },

    toggleVariantSelection(variant) {
      this.hasAttemptedVariantSelection = true;
      if (variant.selected) {
        if (!this.selectedVariants.find((v) => v.id === variant.id)) {
          this.selectedVariants.push(variant);
        }
      } else {
        this.selectedVariants = this.selectedVariants.filter(
          (v) => v.id !== variant.id
        );
      }
    },


    async save() {
      if (!this.$refs.form.validate()) {
        return;
      }

      // Get selected variants for submission
      const variants = this.getSelectedVariantsForSubmission();
      
      // Only require variants if user has actually selected any
      if (this.hasSelectedVariants && variants.length === 0) {
        this.$store.dispatch("errors/setError", {
          message:
            "Please complete all required fields for selected variants.",
          type: "error",
        });
        return;
      }

      try {
        this.isLoading = true;
        this.errors = {};

        const payload = {
          ...this.form,
          seller_product_variants: variants,
        };

        await axios.post("/seller_products", payload);
        this.isLoading = false;
        this.$router.push("/seller-products");
      } catch (error) {
        this.isLoading = false;
        if (error.response && error.response.data.errors) {
          this.errors = error.response.data.errors;
        } else {
          console.error("Error creating seller product:", error);
        }
      }
    },

    getSelectedVariantsForSubmission() {
      const variants = [];

      // Add selected existing variants
      this.productVariants.forEach((variant) => {
        if (
          variant.selected &&
          variant.maximum_retail_price
        ) {
          variants.push({
            variant_type: "existing",
            product_variant_id: variant.id,
            maximum_retail_price: parseFloat(variant.maximum_retail_price),
            seller_discount: variant.seller_discount ? parseFloat(variant.seller_discount) : null,
            ht_discount: variant.ht_discount ? parseFloat(variant.ht_discount) : null,
            consumer_discount: variant.consumer_discount ? parseFloat(variant.consumer_discount) : null,
            doctor_discount: variant.doctor_discount ? parseFloat(variant.doctor_discount) : null,
            stock: parseInt(variant.stock) || 0,
          });
        }
      });

      return variants;
    },
  },

  computed: {
    hasSelectedVariants() {
      return this.productVariants.some((v) => v.selected);
    },
  },
};
</script>

<style scoped>
/* Professional Form Card with Integrated Header */
.form-card {
  background: #ffffff;
  border-radius: 8px;
  overflow: hidden;
  border-left: 4px solid var(--v-primary-base);
}

.page-header {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-bottom: 1px solid #dee2e6;
}

.page-title {
  font-size: 1.25rem;
  font-weight: 600;
  line-height: 1.1;
  margin-bottom: 0.15rem;
}

.page-subtitle {
  font-size: 0.75rem;
  line-height: 1.2;
}

.back-button {
  min-width: 100px;
  height: 32px;
  font-weight: 500;
  text-transform: none;
  font-size: 0.75rem;
}

.form-content {
  background: #ffffff;
}

/* Section Styling */
.form-section {
  border-bottom: 1px solid #f0f0f0;
  padding-bottom: 0.5rem;
}

.form-section:last-child {
  border-bottom: none;
  margin-bottom: 0;
  padding-bottom: 0;
}

.section-title {
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--v-primary-base);
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;
}

/* Professional Form Fields */
.professional-field >>> .v-input__control {
  min-height: 40px;
}

.professional-field >>> .v-text-field__details {
  padding-top: 4px;
}

.professional-field >>> .v-input__slot {
  border-radius: 4px;
}

.professional-field >>> .v-text-field input {
  font-size: 0.75rem;
  padding-top: 8px;
  padding-bottom: 8px;
}

.professional-field >>> .v-label {
  font-size: 0.75rem;
  font-weight: 500;
}

.professional-field >>> .v-icon {
  color: #6c757d !important;
}

/* Status Card */
.status-card {
  border: 2px solid #e9ecef;
  border-radius: 8px;
  transition: all 0.2s ease;
}

.status-card:hover {
  border-color: var(--v-primary-base);
  background-color: #f8f9fa;
}

/* Form Actions */
.form-actions {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-top: 1px solid #dee2e6;
}

.action-button {
  min-width: 90px;
  height: 32px;
  font-weight: 500;
  text-transform: none;
  border-radius: 4px;
  font-size: 0.75rem;
}

/* Spacing and Alignment */
.v-row {
  margin-left: -6px;
  margin-right: -6px;
}

.v-row > .v-col {
  padding: 0 6px;
}

/* Professional Color Scheme */
.v-card {
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08) !important;
}

.v-btn.v-btn--outlined {
  border: 2px solid currentColor;
}

.v-chip {
  font-weight: 500;
}

/* Responsive Design */
@media (max-width: 960px) {
  .page-title {
    font-size: 1.1rem;
  }

  .section-title {
    font-size: 0.85rem;
  }

  .form-content {
    padding: 0.75rem !important;
  }

  .back-button,
  .action-button {
    min-width: 80px;
    font-size: 0.7rem;
  }
}

@media (max-width: 600px) {
  .v-row > .v-col {
    padding: 0 4px;
  }

  .form-content {
    padding: 0.5rem !important;
  }

  .section-title {
    font-size: 0.8rem;
  }

  .professional-field >>> .v-input__control {
    min-height: 36px;
  }

  .professional-field >>> .v-text-field input {
    font-size: 0.7rem;
    padding-top: 6px;
    padding-bottom: 6px;
  }

  .back-button,
  .action-button {
    min-width: 70px;
    height: 28px;
    font-size: 0.65rem;
  }
}

/* Clean Focus States */
.professional-field >>> .v-input--is-focused .v-input__slot {
  border-color: var(--v-primary-base) !important;
  border-width: 2px !important;
}

/* Consistent Margins */
.mb-1 {
  margin-bottom: 0.5rem !important;
}

.mb-2 {
  margin-bottom: 0.75rem !important;
}

.mb-3 {
  margin-bottom: 1rem !important;
}

/* Professional Transitions */
.v-card,
.v-btn,
.professional-field,
.status-card {
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

/* Product Variants Section */
.variants-container {
  max-height: 400px;
  overflow-y: auto;
}

.variant-card {
  margin-bottom: 1rem;
}

.variant-selection-card {
  border: 2px solid #e9ecef;
  border-radius: 8px;
  transition: all 0.2s ease;
}

.variant-selection-card:hover {
  border-color: var(--v-primary-base);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.variant-name {
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--v-primary-base);
  margin-bottom: 0.25rem;
}

.variant-info {
  padding: 0.5rem 0;
}

.variant-details {
  padding-top: 0.5rem;
  border-top: 1px solid #f0f0f0;
}


/* Compact Fields */
.compact-field >>> .v-input__control {
  min-height: 35px;
}

.compact-field >>> .v-text-field input {
  font-size: 0.8rem;
  padding-top: 6px;
  padding-bottom: 6px;
}

.compact-field >>> .v-label {
  font-size: 0.8rem;
}

/* Variant Selection Styles */
.variant-selection-card.selected {
  border-color: var(--v-success-base);
  background-color: #f8fff8;
}

.variant-selection-card.selected .variant-name {
  color: var(--v-success-base);
}

/* Responsive Design for Variants */
@media (max-width: 960px) {
  .variants-container {
    max-height: 300px;
  }

  .variant-name {
    font-size: 0.85rem;
  }

  .compact-field >>> .v-text-field input {
    font-size: 0.75rem;
  }
}

@media (max-width: 600px) {
  .variants-container {
    max-height: 250px;
  }

  .variant-card {
    margin-bottom: 0.75rem;
  }

  .variant-selection-card {
    border-width: 1px;
  }

  .compact-field >>> .v-input__control {
    min-height: 32px;
  }

  .compact-field >>> .v-text-field input {
    font-size: 0.7rem;
    padding-top: 4px;
    padding-bottom: 4px;
  }
}
</style>